import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

const Gallery: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Imagens da galeria
  const galleryImages = [
    { src: '/img/galeria/foto1.jpeg', alt: 'Produtos Sol Nascente 1' },
    { src: '/img/galeria/foto2.jpeg', alt: 'Produtos Sol Nascente 2' },
    { src: '/img/galeria/foto3.jpeg', alt: 'Produtos Sol Nascente 3' },
    { src: '/img/galeria/foto4.jpeg', alt: 'Produtos Sol Nascente 4' },
    { src: '/img/galeria/foto5.jpeg', alt: 'Produtos Sol Nascente 5' },
    { src: '/img/galeria/foto6.jpeg', alt: 'Produtos Sol Nascente 6' },
    { src: '/img/galeria/foto7.jpeg', alt: 'Produtos Sol Nascente 7' },
    { src: '/img/galeria/foto8.jpeg', alt: 'Produtos Sol Nascente 8' },
  ];

  const openLightbox = (index: number) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === galleryImages.length - 1 ? 0 : selectedImage + 1);
    }
  };

  return (
    <section ref={sectionRef} id="gallery" className="py-20 bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
            Galeria de Produtos
          </h2>
          <p className="text-xl text-primary-700 max-w-3xl mx-auto">
            Confira nossos produtos frescos e de qualidade
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Camera className="text-primary-600" size={24} />
            <span className="text-primary-700 font-semibold text-lg">FRESCOR E QUALIDADE EM CADA IMAGEM</span>
            <Camera className="text-primary-600" size={24} />
          </div>
        </div>

        {/* Gallery Grid */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 transition-all duration-1000 delay-400 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          {galleryImages.map((image, index) => (
            <div 
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              
              {/* Overlay de hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Eye size={32} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage !== null && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Controles */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-primary-300 transition-colors duration-200 z-10"
            >
              <X size={32} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-primary-300 transition-colors duration-200 z-10"
            >
              <ChevronLeft size={48} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-primary-300 transition-colors duration-200 z-10"
            >
              <ChevronRight size={48} />
            </button>

            {/* Imagem principal */}
            <div 
              className="relative max-w-4xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={galleryImages[selectedImage].src}
                alt={galleryImages[selectedImage].alt}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>

            {/* Contador */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg">
              {selectedImage + 1} / {galleryImages.length}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
